import React, { useEffect, useRef, useState } from 'react'

import { EditorState } from 'draft-js'

import { Theme, CircularProgress } from '@mui/material'

import { Message } from '../Message/Message'
import { MessageDetail } from '../../types/message'
import { useChannel } from '../../hooks/useChannel'
import { compositeDecorator, RichTextInput } from './RichTextInput'
import { FlexDiv } from '../../components'

interface Props {
    theme: Theme
    channelId?: number
    ignoreScrollToEnd?: boolean
    disableMessaging?: boolean
    afterCreateMessage?: (message: MessageDetail) => void
}

export const MessageForm = (props: Props) => {
    const {
        theme,
        channelId,
        afterCreateMessage,
        ignoreScrollToEnd,
        disableMessaging,
    } = props

    const { messageList, createMessage, channelDetail, loadingState } =
        useChannel(channelId)

    const [editorState, setEditorState] = useState(() =>
        EditorState.createEmpty(compositeDecorator),
    )

    const [fileList, setFileList] = useState<File[]>()
    const endOfMsgRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // Scroll to end of messages
        endOfMsgRef.current && endOfMsgRef.current.scrollIntoView()
    }, [messageList])

    return (
        <FlexDiv
            style={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flex: 1,
                height: '100%',
                padding: 0,
            }}
        >
            <FlexDiv
                style={{
                    margin: theme.spacing(0, 1, 0, 1),
                    padding: theme.spacing(1),
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: theme.shape.borderRadius,
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'flex',
                }}
            >
                {loadingState.messageList ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                        }}
                    >
                        <CircularProgress />
                    </FlexDiv>
                ) : (
                    <>
                        {messageList?.map((msg) => (
                            <Message
                                key={`WORKORDER_MSG_${msg.id}`}
                                message={msg}
                                attachmentSize={160}
                            />
                        ))}
                        {!ignoreScrollToEnd && <div ref={endOfMsgRef} />}
                    </>
                )}
            </FlexDiv>

            <FlexDiv
                style={{
                    marginTop: theme.spacing(1),
                    flexShrink: 0,
                }}
            >
                {!disableMessaging && (
                    <RichTextInput
                        editorState={editorState}
                        currentChannel={channelDetail}
                        fileList={fileList}
                        disableMessaging={disableMessaging}
                        setEditorState={setEditorState}
                        setFileList={setFileList}
                        createMessage={createMessage}
                        onCreateMessage={afterCreateMessage}
                    />
                )}
            </FlexDiv>
        </FlexDiv>
    )
}
